import React from 'react';
import moment from 'moment';
import { StreakReward } from '../../../api/API';
import { stripHtml } from '../../../util';
import './StreakRewardsList.css';

interface StreakRewardsListProps {
  rewards?: StreakReward[];
  compact?: boolean;
}

const groupRewards = (rewards: StreakReward[] = []) => {
  const available = rewards.filter(reward => reward.status !== 'redeemed' && reward.status !== 'expired');
  const used = rewards.filter(reward => reward.status === 'redeemed');
  return { available, used };
};

const formatDate = (date?: string | null) => {
  if (!date) return null;
  return moment(date).format('DD.MM.YYYY');
};

const expiresSoon = (reward: StreakReward) => {
  if (!reward.expiresAt || reward.status === 'redeemed' || reward.status === 'expired') return false;
  const daysLeft = moment(reward.expiresAt).diff(moment(), 'days');
  return daysLeft >= 0 && daysLeft <= 7;
};

const RewardItem: React.FC<{ reward: StreakReward; compact?: boolean; statusLabel?: string; showDescription?: boolean }> = ({ reward, compact, statusLabel, showDescription }) => (
  <div className={`streak-reward-item ${expiresSoon(reward) ? 'expires-soon' : ''}`}>
    <div className="streak-reward-main">
      <div className="streak-reward-title-row">
        <span className="streak-reward-name">{stripHtml(reward.rewardName || 'Streak reward')}</span>
        <span className="streak-reward-week">{reward.milestoneWeek}w</span>
      </div>
      {(showDescription || !compact) && reward.rewardDescription && (
        <p className="streak-reward-description">{stripHtml(reward.rewardDescription)}</p>
      )}
      <div className="streak-reward-meta">
        {statusLabel && <span>{statusLabel}</span>}
        {reward.status === 'redeemed' && reward.redeemedAt && (
          <span>Used {formatDate(reward.redeemedAt)}</span>
        )}
        {reward.status !== 'redeemed' && reward.expiresAt && (
          <span className={expiresSoon(reward) ? 'streak-reward-expiry urgent' : 'streak-reward-expiry'}>
            Expires {formatDate(reward.expiresAt)}
          </span>
        )}
      </div>
    </div>
  </div>
);

const RewardGroup: React.FC<{
  title: string;
  rewards: StreakReward[];
  emptyText: string;
  compact?: boolean;
  statusLabel?: string;
  showDescription?: boolean;
}> = ({ title, rewards, emptyText, compact, statusLabel, showDescription }) => (
  <div className="streak-reward-group">
    <div className="streak-reward-group-title">
      <span>{title}</span>
      <span className="streak-reward-count">{rewards.length}</span>
    </div>
    {rewards.length > 0 ? (
      <div className="streak-reward-items">
        {rewards.map(reward => (
          <RewardItem key={reward.id} reward={reward} compact={compact} statusLabel={statusLabel} showDescription={showDescription} />
        ))}
      </div>
    ) : (
      !compact && <div className="streak-reward-empty">{emptyText}</div>
    )}
  </div>
);

const StreakRewardsList: React.FC<StreakRewardsListProps> = ({ rewards = [], compact = false }) => {
  const groups = groupRewards(rewards);
  const hasRewards = groups.available.length > 0 || groups.used.length > 0;

  if (!hasRewards) {
    return (
      <div className={`streak-rewards-list ${compact ? 'compact' : ''}`}>
        <div className="streak-reward-empty">No streak rewards yet.</div>
      </div>
    );
  }

  return (
    <div className={`streak-rewards-list ${compact ? 'compact' : ''}`}>
      <RewardGroup
        title="Available"
        rewards={groups.available}
        emptyText="No available rewards right now."
        compact={compact}
        statusLabel="Available"
        showDescription
      />
      <RewardGroup
        title="Used"
        rewards={groups.used}
        emptyText="No used rewards yet."
        compact={compact}
      />
    </div>
  );
};

export default StreakRewardsList;
